<template>
  <div class="campground">
    <div class="container-fluid">
      <div class="row no-gutters">
        <div class="col-12 col-lg-4">
          <CiCampgroundInfo
            :campground="campground"
            :countries="countries"
            :currency="currency"
            :dates="dates"
            :guests="selectedPersons"
            :setStepStatus="setStepStatus"
            :toggleBookingDetails="toggleBookingDetails"
            :urls="urls">
            <CiSum
              v-if="price.total"
              :campgroundCurrency="campground.currency"
              :isCompleted="false"
              :language="language"
              :price="price"
              :userCurrency="currency"
              slot="sum">
            </CiSum>
          </CiCampgroundInfo>
        </div>
        <div class="col-12 col-lg-8">
          <div class="campground__interaction">
            <CiLanguageChooser
              v-if="activeStep < 6"
              :language="language" :currency="currency"
              :languages="campground.languages" :currencies="currencies"
              @setLanguage="onChangeLanguage"
              @setCurrency="setCurrency">
            </CiLanguageChooser>

            <CiProgressBar :activeStep="activeStep" @changeStep="onChangeStep"></CiProgressBar>

            <transition :name="transitionName" mode="out-in">
              <router-view :key="$route.path"></router-view>
            </transition>

            <CiFooter :campground="campground"></CiFooter>

            <CiSum @click.native="toggleBookingDetails"
              v-if="price.total"
              :campgroundCurrency="campground.currency"
              :isCompleted="false"
              :isMobile="true"
              :language="language"
              :price="price"
              :userCurrency="currency">
            </CiSum>

            <CiCountdown
              :started="checkout.responseCheckout.created"
              :session-timeout="sessionTimeout"
              :start-over="startOver">
            </CiCountdown>
          </div>
        </div>
      </div>
    </div>
    <CiModal></CiModal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  CiCampgroundInfo,
  CiCountdown,
  CiFooter,
  CiLanguageChooser,
  CiModal,
  CiProgressBar,
  CiProgressNavigation,
  CiSum,
} from '@/components'

export default {
  name: 'Campground',
  components: {
    CiCampgroundInfo,
    CiCountdown,
    CiFooter,
    CiLanguageChooser,
    CiModal,
    CiProgressBar,
    CiProgressNavigation,
    CiSum,
  },
  data() {
    return {
      transitionName: 'fade',
      to: null,
    }
  },
  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      currency: 'currency',
      language: 'language',
      steps: 'steps',
    }),

    ...mapGetters('campground', {
      campground: 'campground',
      countries: 'countries',
      currencies: 'currencies',
      sessionTimeout: 'sessionTimeout',
      status: 'status',
      urls: 'urls',
    }),

    ...mapGetters('booking', {
      checkout: 'checkout',
      dates: 'dates',
      price: 'price',
      selectedCampsite: 'selectedCampsite',
      selectedPersons: 'selectedPersons',
    }),

    hasValidCampsiteSelection() {
      return this.steps[2].selectedCampsiteIsAvailable
    },
  },
  methods: {
    ...mapActions('application', {
      resetStepStatus: 'resetStepStatus',
      setCurrency: 'setCurrency',
      setLoader: 'setLoader',
      setStepStatus: 'setStepStatus',
      toggleBookingDetails: 'toggleBookingDetails',
    }),

    ...mapActions('booking', {
      resetState: 'resetState',
    }),

    onChangeStep(step) {
      this.$router.push({
        name: 'Step',
        params: { ...this.$route.params, step: step },
      })
    },

    onChangeLanguage(value) {
      this.setLoader(true)
      this.$router.push({
        name: this.$router.currentRoute.name,
        params: { ...this.$route.params, language: value.code },
      })
    },

    onSelectedCampsiteValidationChange(hasValidCampsiteSelection) {
      /* istanbul ignore else */
      if (!hasValidCampsiteSelection) {
        this.$modal.show('dialog', {
          buttons: {
            primary: { active: true, classes: 'button--primary button--center' },
            secondary: { active: false },
          },
          title: this.$t('modals.CiCampsiteNotAvailable.title'),
          content: this.$t('modals.CiCampsiteNotAvailable.content'),
        })
      }
    },

    startOver() {
      /* go back to step 1 */
      this.$router.push({
        name: 'Step',
        params: { ...this.$route.params, step: 1 },
      })

      /* reset booking state */
      this.resetState()

      /* reset step status */
      this.resetStepStatus()
    },
  },
  watch: {
    hasValidCampsiteSelection: {
      handler: 'onSelectedCampsiteValidationChange',
      immediate: true,
    },

    $route(to, from) {
      /* istanbul ignore next */
      this.transitionName =
        to.params.step > from.params.step
          ? 'slide-next'
          : to.params.step < from.params.step
            ? 'slide-prev'
            : ''
    },
  },
}
</script>

<style lang="scss" scoped>
.campground {
  min-height: 100vh;

  @include media-breakpoint-up(lg) {
    > .container-fluid {
      padding: 0;
    }

    > .container-fluid,
    > .container-fluid > .row {
      min-height: 100vh;
    }
  }
}

.campground__interaction {
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow: hidden;
  padding: 20px 0 0;
  position: relative;

  @include media-breakpoint-up(md) {
    padding: 0 20px 0;
  }

  @include media-breakpoint-up(lg) {
    padding: 0 30px 0;
  }

  @include media-breakpoint-up(xl) {
    padding: 0 35px 0;
  }
}
</style>
